<template>
  <div class="pages-ccd-company-detail">
    <div class="page-head-container">
      <div class="page-head clearfix">
        <div class="page-head-info columns clearfix">
          <div class="nav-bar">
            <a @click="navBack()">
              &lt; {{ $t('back') }}</a>
          </div>
          <div class="page-head-project-info clearfix">
            <div
              class="page-project-number"
              v-if="currentCompany.id"
            >
              {{ $i18n.t('company.companyNumber') }} {{ currentCompany.id || '' }}
            </div>
            <div class="row page-project-name">
              <div>{{ currentCompany.name || $t(description) }}</div>
            </div>
          </div>
          <div class="float-right clearfix">
            <button
              type="button"
              class="btn btn--orange"
              v-if="allowEditCompany && !allowEdit"
              v-hkldreset
              @click="enableEdit()"
            >{{ $t('action.edit') }}</button>
            <button
              type="button"
              class="btn btn--darkblue"
              v-if="allowEdit"
              v-hkldreset
              @click="cancelEdit()"
            >{{ $t('action.cancel') }}</button>
            <button
              type="button"
              class="btn btn--orange"
              v-if="allowEdit"
              v-hkldreset
              @click="save()"
            >{{ $t('action.save') }}</button>
          </div>
        </div>
      </div>
    </div>
    <CompanyInformation
      ref="companyInformation"
      :allowEdit="allowEdit"
    />
    <VendorPerformance v-if="showPerformance" />
    <CompanyTenancyRecord
      :allowCreate="allowCreateTenancyRecord"
      v-if="showTenancyRecord"
      :tenancies="tenancies"
    />
    <CompanyContact
      :allowEdit="allowEdit"
      v-if="currentCompany.id"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'

import CompanyInformation from '@/modules/ccd/companies/CompanyInformation'
import CompanyContact from '@/modules/ccd/companies/CompanyContact'
import CompanyTenancyRecord from '@/modules/ccd/companies/CompanyTenancyRecord'
import VendorPerformance from '@/modules/ccd/companies/VendorPerformance'

import mutationTypes from '@/store/mutation-types'
import navigation from '@/utils/navigation'
import { aclHelper, ccdPermsTypes } from '@/utils/aclHelper'
import { ccdRouteNames } from '@/utils/consts'
import { companyType } from '@/utils/locale'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import CmsModulesStatusMixin from '@/modules/CmsModulesStatusMixin'

export default {
  name: 'CompanyDetail',
  components: {
    CompanyContact,
    CompanyInformation,
    CompanyTenancyRecord,
    VendorPerformance,
  },
  mixins: [CmsModulesStatusMixin],
  data () {
    return {
      allowEdit: false,
      hasSave: false,
      description: 'company.createNewCompany',
      deep,
      keep: {},
      inputRequireHighlight: false,
      companyId: null,
    }
  },
  methods: {
    ...mapActions('ccd', [
      'loadCompany',
      'createCompany',
      'updateCompany',
      'updateCompanyContacts',
      'removeCompanyContacts',
    ]),
    ...mapMutations('ccd', {
      setCurrentCompany: mutationTypes.COMPANY_UPDATE_CURRENT,
    }),
    enableEdit () {
      navigation.replacePage('companyEdit', { id: this.companyId })
      this.keep = lodash.cloneDeep(this.currentCompany)
      this.allowEdit = true
    },
    cancelEdit () {
      formUtils.resetDirty()
      if (!this.currentCompany.id) {
        this.$router.goBack()
        return false
      }
      this.inputRequireHighlight = false
      this.setCurrentCompany({ company: this.keep })
      this.allowEdit = false
      navigation.replacePage(ccdRouteNames.companyView, { id: this.companyId })
    },

    validate () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'partial'}, '.pages-ccd-company-detail')
      return $('.pages-ccd-company-detail input.is-invalid-input').length === 0 &&
        fusionInputCheckUtil.isAllFieldsValid('.pages-ccd-company-detail')
    },

    getRemovedContactsList () {
      if (!(Array.isArray(this.keep.ccdcontacts) && Array.isArray(this.currentCompany.ccdcontacts))) {
        return []
      }
      const originCcdcontacts = this.keep.ccdcontacts.map((item) => item.id)
      const AfterDeletedCcdcontacts = this.currentCompany.ccdcontacts.map((item) => item.id)
      let deletedCcdcontacts = []
      originCcdcontacts.forEach((id) => {
        if (!AfterDeletedCcdcontacts.includes(id)) {
          deletedCcdcontacts.push(id)
        }
      })
      return deletedCcdcontacts
    },

    buildContactRequest (ccdcontacts) {
      let contactRequest = []
      if (Array.isArray(ccdcontacts)) {
        ccdcontacts.forEach((item) => {
          for (let i = 0; i < this.keep.ccdcontacts.length; i++) {
            if ((this.keep.ccdcontacts[i].id === item.id)) {
              const { title, department, isPropertyPerson } = item.CompanyContact
              if (this.keep.ccdcontacts[i].CompanyContact.title !== title ||
                this.keep.ccdcontacts[i].CompanyContact.department !== department ||
                this.keep.ccdcontacts[i].CompanyContact.isPropertyPerson !== isPropertyPerson) {
                contactRequest.push({title, department, isPropertyPerson, ccdContactId: item.id})
              }
            }
          }
        })
      }
      return contactRequest
    },
    async save () {
      if (this.currentCompany.type && [companyType.O, undefined].includes(this.currentCompany.type)) {
        fusionInputCheckUtil.resetAllFields()
        if (!(this.validate() && this.$refs.companyInformation.hasAtLeastOneContact())) {
          return false
        }
      }
      if (!this.currentCompany.id) {
        await this.createCompany()
        formUtils.resetDirty()
        if (this.currentCompany.id) {
          this.companyId = this.currentCompany.id
          this.$router.replace({
            name: ccdRouteNames.companyView,
            params: { id: this.currentCompany.id },
          })
        }
      } else {
        await this.updateCompany({
          companyId: this.currentCompany.id,
          request: this.currentCompany,
        })
        const updateContactRquest = this.buildContactRequest(this.currentCompany.ccdcontacts)
        if (updateContactRquest.length) {
          await this.updateCompanyContacts({
            companyId: this.currentCompany.id,
            request: updateContactRquest,
          })
        }
        const removeContacts = this.getRemovedContactsList()
        if (removeContacts.length) {
          await this.removeCompanyContacts({ companyId: this.currentCompany.id, request: {ccdContactIds: removeContacts} })
        }
        if (this.currentCompany.id) {
          await this.loadCompany(this.currentCompany.id)
        }
        navigation.replacePage(ccdRouteNames.companyView, { id: this.currentCompany.id })
      }
      this.keep = lodash.cloneDeep(this.currentCompany)
      formUtils.resetDirty()
      this.allowEdit = false
    },
    navBack () {
      this.allowEdit = false
      this.$router.goBack()
    },
  },
  async mounted () {
    this.companyId = this.$router.currentRoute.params.id
    const routeName = this.$router.currentRoute.name
    if (routeName === ccdRouteNames.companyView) {
      await this.loadCompany(this.companyId)
    } else if (routeName === ccdRouteNames.companyEdit) {
      await this.loadCompany(this.companyId)
      this.keep = lodash.cloneDeep(this.currentCompany)
      this.allowEdit = true
    } else {
      this.setCurrentCompany({company: { type: companyType.O }})
      this.allowEdit = true
    }
  },
  computed: {
    ...mapGetters('ccd', ['currentCompany']),
    ...mapGetters('regions', ['enableVendorPerformance']),

    showTenancyRecord () {
      return this.currentCompany.type && (companyType.T === this.currentCompany.type)
    },

    showPerformance () {
      const mustHaveCondition = this.enableVendorPerformance
      return mustHaveCondition && this.currentCompany.type && (companyType.C === this.currentCompany.type)
    },

    allowEditCompany () {
      if (!(companyType.C === this.currentCompany.type && aclHelper.getAccess(ccdPermsTypes.contractor.createUpdate)) &&
        !(companyType.T === this.currentCompany.type && aclHelper.getAccess(ccdPermsTypes.tenant.createUpdate)) &&
        !(companyType.O === this.currentCompany.type && aclHelper.getAccess(ccdPermsTypes.organization.createUpdate))
      ) {
        return false
      }
      return true
    },

    allowCreateTenancyRecord () {
      return this.allowEdit && aclHelper.getAccess(ccdPermsTypes.tenancy.create)
    },
    tenancies () {
      return this.currentCompany.tenancies || []
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~styles/common-header";
</style>
